import React, { Fragment, useState, useRef, useEffect } from 'react';
import { Heading, Link } from '@fiverr-private/typography';
import { Video } from '@fiverr-private/media';
import { AspectRatio, Container } from '@fiverr-private/layout_components';
import * as styles from './styles.ve.css';

export interface CategoryPreviewCardProps {
  videoSrc: string;
  posterSrc: string;
  title: string;
  href: string;
  onClick?: () => void;
  overlayColor?: string;
}

const CategoryPreviewCard: React.FC<CategoryPreviewCardProps> = ({
  videoSrc,
  posterSrc,
  title,
  href,
  onClick,
  overlayColor = '0, 0, 0',
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 600);
    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      if (isHovered) {
        videoRef.current.play().catch(() => {
          // Ignore autoplay errors
        });
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isHovered]);

  const handleMouseEnter = () => {
    if (!isMobile) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <AspectRatio ratio="1" className={styles.card} data-carousel-item="">
      <Link
        href={href}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={onClick}
        className={styles.linkInner}
        overflow="hidden"
      >
        <Video
          ref={videoRef}
          src={videoSrc}
          poster={posterSrc}
          muted
          loop
          playsInline
          controls={false}
          width="100%"
          height="100%"
          objectFit="cover"
          display="block"
        />
        <Container
          position="absolute"
          left={0}
          right={0}
          bottom={0}
          height="50%"
          pointerEvents="none"
          style={{
            background: `linear-gradient(180deg, rgba(${overlayColor}, 0) 0%, rgba(${overlayColor}, 0.9) 90%)`,
          }}
        />
        <Heading
          as="h3"
          color="white"
          fontWeight="normal"
          position="absolute"
          left={0}
          bottom={0}
          width="100%"
          className={styles.heading}
          zIndex="1"
        >
          {title.split('\n').map((line, index) => (
            <Fragment key={index}>
              {line}
              {index < title.split('\n').length - 1 && <br />}
            </Fragment>
          ))}
        </Heading>
      </Link>
    </AspectRatio>
  );
};

export default CategoryPreviewCard;
